/**
 * FL-118: 日次レポート統合サービス
 * 設定管理・レポート生成・メール送信を統合した機能
 */

import { addDays, format } from 'date-fns';
import { getSupabaseClient } from '../utils/supabase';
import { reportGeneratorService } from './reportGeneratorService';
import { emailService } from './emailService';
import logger from '../utils/logger';
import type { DailyReportSettings, DailyReportLog, ReportFileFormat } from '../types';

export interface DailyReportExecutionOptions {
  companyId: string;
  reportDate?: Date;
  forceExecute?: boolean; // 重複実行を許可するか
  testMode?: boolean; // テストモード（実際には送信しない）
}

export interface DailyReportExecutionResult {
  success: boolean;
  logId?: string;
  error?: string;
  executionTimeMs: number;
  filesGenerated: number;
  recipientsCount: number;
  fileSizes: Record<ReportFileFormat, number>;
}

export class DailyReportService {
  /**
   * 会社の日次レポート設定を取得
   */
  async getCompanySettings(companyId: string): Promise<DailyReportSettings | null> {
    try {
      const supabase = getSupabaseClient();
      if (!supabase) throw new Error('Supabaseクライアントが利用できません');

      // 認証状態デバッグログ
      const {
        data: { session },
      } = await supabase.auth.getSession();
      logger.info('🔍 日次レポート設定取得 - 認証状態確認', {
        companyId,
        hasSession: !!session,
        userId: session?.user?.id,
        userEmail: session?.user?.email,
        accessToken: session?.access_token?.substring(0, 20) + '...',
      });

      // RLS無効化中のため、company_idフィルターを再追加（診断用）
      const { data, error } = await supabase
        .from('daily_report_settings')
        .select('*')
        .eq('company_id', companyId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // データが存在しない場合はnullを返す
          return null;
        }
        throw error;
      }

      return this.mapDatabaseToSettings(data);
    } catch (error) {
      logger.error('日次レポート設定取得エラー', { companyId, error });
      throw error;
    }
  }

  /**
   * 日次レポート設定を保存
   */
  async saveCompanySettings(settings: Partial<DailyReportSettings>): Promise<DailyReportSettings> {
    try {
      const supabase = getSupabaseClient();
      if (!supabase) throw new Error('Supabaseクライアントが利用できません');

      const dbData = this.mapSettingsToDatabase(settings);

      // 会社IDがRLSポリシーで自動設定されるため、手動設定
      if (!dbData.company_id && settings.companyId) {
        dbData.company_id = settings.companyId;
      }

      const { data, error } = await supabase
        .from('daily_report_settings')
        .upsert(dbData, {
          onConflict: 'company_id',
        })
        .select()
        .single();

      if (error) {
        throw error;
      }

      logger.info('日次レポート設定保存完了', {
        companyId: settings.companyId,
        reportType: settings.reportType,
        isActive: settings.isActive,
      });

      return this.mapDatabaseToSettings(data);
    } catch (error) {
      logger.error('日次レポート設定保存エラー', { settings, error });
      throw error;
    }
  }

  /**
   * 日次レポートを実行
   */
  async executeDailyReport(
    options: DailyReportExecutionOptions
  ): Promise<DailyReportExecutionResult> {
    const startTime = performance.now();
    const reportDate = options.reportDate || new Date();
    const reportDateStr = format(reportDate, 'yyyy-MM-dd');

    try {
      logger.info('日次レポート実行開始', {
        companyId: options.companyId,
        reportDate: reportDateStr,
        testMode: options.testMode,
      });

      // 設定取得
      const settings = await this.getCompanySettings(options.companyId);
      if (!settings || !settings.isActive) {
        throw new Error('日次レポート設定が無効または存在しません');
      }

      // 重複実行チェック
      if (!options.forceExecute && !options.testMode) {
        const existingLog = await this.checkExistingLog(options.companyId, reportDateStr);
        if (existingLog) {
          throw new Error(`指定日のレポートは既に送信済みです: ${reportDateStr}`);
        }
      }

      // 対象期間計算
      const startDate = reportDate;
      const endDate = addDays(reportDate, settings.dateRangeDays - 1);

      // レポートデータ取得
      const reportData = await reportGeneratorService.getReportData(
        options.companyId,
        startDate,
        endDate
      );

      // ファイル生成
      const fileFormats: ReportFileFormat[] = [];
      if (settings.includeCsv) fileFormats.push('csv');
      if (settings.includePdf) fileFormats.push('pdf');

      const attachments = await reportGeneratorService.generateReportFiles(reportData, fileFormats);

      // メール送信（テストモードでない場合）
      let emailResult;
      if (!options.testMode && settings.sendToEmails.length > 0) {
        // レポートデータを新しいemailServiceの形式に変換
        const reportDataForEmail = {
          reportDate: format(reportDate, 'yyyy年MM月dd日'),
          csvFilePath: attachments.find(a => a.filename.endsWith('.csv'))?.filename,
          pdfFilePath: attachments.find(a => a.filename.endsWith('.pdf'))?.filename,
          summary: {
            totalCases: reportData.summary?.totalCases || 0,
            totalRevenue: 0, // TODO: 現時点で収益計算は未実装のためデフォルト値
            totalDistance: 0, // TODO: 現時点で距離計算は未実装のためデフォルト値
          },
        };

        emailResult = await emailService.sendDailyReportEmail(
          options.companyId,
          settings.sendToEmails,
          reportDataForEmail
        );
      } else {
        emailResult = { success: true };
      }

      const endTime = performance.now();
      const executionTimeMs = Math.round(endTime - startTime);

      // 実行ログ記録
      const logId = options.testMode
        ? null
        : await this.saveExecutionLog({
            companyId: options.companyId,
            reportDate: reportDateStr,
            reportType: settings.reportType,
            recipients: settings.sendToEmails,
            fileFormats,
            status: emailResult.success ? 'success' : 'failed',
            errorMessage: emailResult.error,
            executionTimeMs,
            dateRangeStart: format(startDate, 'yyyy-MM-dd'),
            dateRangeEnd: format(endDate, 'yyyy-MM-dd'),
            casesCount: reportData.summary.totalCases,
            driversCount: reportData.summary.totalDrivers,
            csvFileSizeBytes: attachments.find(a => a.filename.includes('.csv'))?.size,
            pdfFileSizeBytes: attachments.find(a => a.filename.includes('.pdf'))?.size,
          });

      const fileSizes = attachments.reduce(
        (acc, att) => {
          if (att.filename.includes('.csv')) acc.csv = att.size;
          if (att.filename.includes('.pdf')) acc.pdf = att.size;
          return acc;
        },
        {} as Record<ReportFileFormat, number>
      );

      logger.info('日次レポート実行完了', {
        companyId: options.companyId,
        reportDate: reportDateStr,
        success: emailResult.success,
        executionTimeMs,
        filesGenerated: attachments.length,
        recipientsCount: settings.sendToEmails.length,
      });

      return {
        success: emailResult.success,
        logId: logId || undefined,
        error: emailResult.error || undefined,
        executionTimeMs,
        filesGenerated: attachments.length,
        recipientsCount: settings.sendToEmails.length,
        fileSizes,
      };
    } catch (error) {
      const endTime = performance.now();
      const executionTimeMs = Math.round(endTime - startTime);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      // エラーログ記録
      if (!options.testMode) {
        await this.saveExecutionLog({
          companyId: options.companyId,
          reportDate: reportDateStr,
          reportType: 'combined',
          recipients: [],
          fileFormats: [],
          status: 'failed',
          errorMessage,
          executionTimeMs,
          dateRangeStart: reportDateStr,
          dateRangeEnd: reportDateStr,
          casesCount: 0,
          driversCount: 0,
        });
      }

      logger.error('日次レポート実行エラー', {
        companyId: options.companyId,
        reportDate: reportDateStr,
        error: errorMessage,
        executionTimeMs,
      });

      return {
        success: false,
        error: errorMessage,
        executionTimeMs,
        filesGenerated: 0,
        recipientsCount: 0,
        fileSizes: { csv: 0, pdf: 0 },
      };
    }
  }

  /**
   * テストメール送信 (シンプルアーキテクチャ)
   */
  async sendTestEmail(
    companyId: string,
    recipientEmail: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      logger.info('テストメール送信開始（シンプルアーキテクチャ）', {
        companyId,
        recipientEmail,
      });

      const result = await emailService.sendTestEmail(companyId, recipientEmail);

      if (!result.success) {
        logger.error('テストメール送信失敗', {
          companyId,
          recipientEmail,
          error: result.error,
        });

        return {
          success: false,
          error: result.error || 'メール送信に失敗しました',
        };
      }

      logger.info('テストメール送信成功', {
        companyId,
        recipientEmail,
        messageId: result.messageId,
      });

      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);

      logger.error('テストメール送信エラー', {
        companyId,
        recipientEmail,
        error: errorMessage,
      });

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * 送信履歴取得
   */
  async getExecutionLogs(
    companyId: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<DailyReportLog[]> {
    try {
      const supabase = getSupabaseClient();
      if (!supabase) throw new Error('Supabaseクライアントが利用できません');

      const { data, error } = await supabase
        .from('daily_report_logs')
        .select('*')
        .order('sent_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) {
        throw error;
      }

      return (data || []).map(this.mapDatabaseToLog);
    } catch (error) {
      logger.error('送信履歴取得エラー', { companyId, error });
      throw error;
    }
  }

  /**
   * 送信履歴の統計取得
   */
  async getExecutionStats(
    companyId: string,
    days: number = 30
  ): Promise<{
    totalExecutions: number;
    successfulExecutions: number;
    failedExecutions: number;
    successRate: number;
    averageExecutionTime: number;
  }> {
    try {
      const supabase = getSupabaseClient();
      if (!supabase) throw new Error('Supabaseクライアントが利用できません');

      const cutoffDate = format(addDays(new Date(), -days), 'yyyy-MM-dd');

      const { data, error } = await supabase
        .from('daily_report_logs')
        .select('status, execution_time_ms')
        .gte('report_date', cutoffDate);

      if (error) {
        throw error;
      }

      const logs = data || [];
      const totalExecutions = logs.length;
      const successfulExecutions = logs.filter(log => log.status === 'success').length;
      const failedExecutions = totalExecutions - successfulExecutions;
      const successRate = totalExecutions > 0 ? (successfulExecutions / totalExecutions) * 100 : 0;

      const avgExecutionTime =
        logs.length > 0
          ? logs.reduce((sum, log) => sum + (log.execution_time_ms || 0), 0) / logs.length
          : 0;

      return {
        totalExecutions,
        successfulExecutions,
        failedExecutions,
        successRate: Math.round(successRate * 100) / 100,
        averageExecutionTime: Math.round(avgExecutionTime),
      };
    } catch (error) {
      logger.error('送信統計取得エラー', { companyId, error });
      throw error;
    }
  }

  private async checkExistingLog(_companyId: string, reportDate: string): Promise<boolean> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabaseクライアントが利用できません');

    const { data } = await supabase
      .from('daily_report_logs')
      .select('id')
      .eq('report_date', reportDate)
      .eq('status', 'success')
      .limit(1)
      .single();

    return data !== null;
  }

  private async saveExecutionLog(logData: Partial<DailyReportLog>): Promise<string> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabaseクライアントが利用できません');

    const { data, error } = await supabase
      .from('daily_report_logs')
      .insert({
        company_id: logData.companyId,
        report_date: logData.reportDate,
        report_type: logData.reportType,
        recipients: logData.recipients,
        file_formats: logData.fileFormats,
        status: logData.status,
        error_message: logData.errorMessage,
        execution_time_ms: logData.executionTimeMs,
        retry_count: 0,
        date_range_start: logData.dateRangeStart,
        date_range_end: logData.dateRangeEnd,
        csv_file_size_bytes: logData.csvFileSizeBytes,
        pdf_file_size_bytes: logData.pdfFileSizeBytes,
        cases_count: logData.casesCount,
        drivers_count: logData.driversCount,
      })
      .select('id')
      .single();

    if (error) {
      throw error;
    }

    return data.id;
  }

  private mapDatabaseToSettings(data: any): DailyReportSettings {
    return {
      id: data.id,
      companyId: data.company_id,
      reportType: data.report_type,
      sendTime: data.send_time,
      sendToEmails: data.send_to_emails,
      includeCsv: data.include_csv,
      includePdf: data.include_pdf,
      dateRangeDays: data.date_range_days,
      isActive: data.is_active,
      emailSubjectTemplate: data.email_subject_template,
      emailBodyTemplate: data.email_body_template,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      createdBy: data.created_by,
      updatedBy: data.updated_by,
    };
  }

  private mapSettingsToDatabase(settings: Partial<DailyReportSettings>): any {
    return {
      company_id: settings.companyId,
      report_type: settings.reportType,
      send_time: settings.sendTime,
      send_to_emails: settings.sendToEmails,
      include_csv: settings.includeCsv,
      include_pdf: settings.includePdf,
      date_range_days: settings.dateRangeDays,
      is_active: settings.isActive,
      email_subject_template: settings.emailSubjectTemplate,
      email_body_template: settings.emailBodyTemplate,
      updated_by: settings.updatedBy,
      created_by: settings.createdBy,
    };
  }

  private mapDatabaseToLog(data: any): DailyReportLog {
    return {
      id: data.id,
      companyId: data.company_id,
      reportDate: data.report_date,
      reportType: data.report_type,
      recipients: data.recipients,
      fileFormats: data.file_formats,
      status: data.status,
      errorMessage: data.error_message,
      fileSizes: data.file_sizes,
      executionTimeMs: data.execution_time_ms,
      retryCount: data.retry_count,
      dateRangeStart: data.date_range_start,
      dateRangeEnd: data.date_range_end,
      csvFileSizeBytes: data.csv_file_size_bytes,
      pdfFileSizeBytes: data.pdf_file_size_bytes,
      casesCount: data.cases_count,
      driversCount: data.drivers_count,
      sentAt: data.sent_at,
      createdBy: data.created_by,
    };
  }
}

// シングルトンインスタンス
export const dailyReportService = new DailyReportService();
